import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Marker, Polygon, Tooltip, useMap } from "react-leaflet";
import styles from "styles/component/ImageOverlayTool.module.scss";
import {
  rotatedCorners,
  rotatedEdgeMidpoints,
  rotationFromHandleDrag,
  rotationHandlePosition,
  scaleBoundsFromCorner,
  stretchBoundsEdge,
  translateBounds,
  unrotatePoint,
} from "./overlayGeometry";

const CORNER_CURSORS = {
  nw: "nwse-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  se: "nwse-resize",
};

const EDGE_CURSORS = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
};

const buildHandleIcon = (cursor) =>
  L.divIcon({
    className: "",
    html: `<div class="${styles.overlay_handle}" style="cursor:${cursor}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

const CORNER_ICONS = Object.freeze(
  Object.fromEntries(
    Object.entries(CORNER_CURSORS).map(([corner, cursor]) => [
      corner,
      buildHandleIcon(cursor),
    ])
  )
);

const EDGE_ICONS = Object.freeze(
  Object.fromEntries(
    Object.entries(EDGE_CURSORS).map(([edge, cursor]) => [
      edge,
      buildHandleIcon(cursor),
    ])
  )
);

const ROTATE_ICON = L.divIcon({
  className: "",
  html: `<div class="${styles.overlay_rotate_handle}"><svg viewBox="0 0 24 24" width="14" height="14"><path fill="currentColor" d="M15.55 5.55 11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zM13 17.91v2.02c1.39-.17 2.74-.71 3.9-1.61l-1.44-1.44c-.75.54-1.59.89-2.46 1.03zm3.89-2.42 1.42 1.41c.9-1.16 1.45-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.48z"/></svg></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const ImageOverlayEditor = ({
  overlay,
  setOverlayBounds,
  setOverlayRotation,
}) => {
  const map = useMap();
  const [draggingKey, setDraggingKey] = useState(null);
  const frozenPositionRef = useRef(null);
  const bodyDragCleanupRef = useRef(null);

  useEffect(
    () => () => {
      if (bodyDragCleanupRef.current) {
        bodyDragCleanupRef.current();
      }
    },
    []
  );

  const corners = rotatedCorners(overlay.bounds, overlay.rotation);
  const edges = rotatedEdgeMidpoints(overlay.bounds, overlay.rotation);
  const rotateHandle = rotationHandlePosition(overlay.bounds, overlay.rotation);

  const startHandleDrag = (key, position) => {
    frozenPositionRef.current = position;
    setDraggingKey(key);
  };

  const endHandleDrag = () => {
    frozenPositionRef.current = null;
    setDraggingKey(null);
  };

  const onBodyMouseDown = (e) => {
    if (e.originalEvent.button !== 0) return;
    if (bodyDragCleanupRef.current) return;
    L.DomEvent.stop(e.originalEvent);
    map.dragging.disable();
    const origin = e.latlng;
    const baseBounds = overlay.bounds;
    const handleMove = (moveEvent) => {
      const latlng = map.mouseEventToLatLng(moveEvent);
      const dLat = latlng.lat - origin.lat;
      const dLng = latlng.lng - origin.lng;
      setOverlayBounds(overlay.id, translateBounds(baseBounds, dLat, dLng));
    };
    const stopDrag = () => {
      L.DomEvent.off(document, "mousemove", handleMove);
      L.DomEvent.off(document, "mouseup", handleUp);
      window.removeEventListener("blur", stopDrag);
      bodyDragCleanupRef.current = null;
      map.dragging.enable();
    };
    const handleUp = (upEvent) => {
      if (upEvent.button !== 0) return;
      stopDrag();
    };
    L.DomEvent.on(document, "mousemove", handleMove);
    L.DomEvent.on(document, "mouseup", handleUp);
    window.addEventListener("blur", stopDrag);
    bodyDragCleanupRef.current = stopDrag;
  };

  const onRotateMouseDown = (e) => {
    if (e.originalEvent.button !== 0) return;
    if (bodyDragCleanupRef.current) return;
    L.DomEvent.stop(e.originalEvent);
    map.dragging.disable();
    const handleMove = (moveEvent) => {
      const latlng = map.mouseEventToLatLng(moveEvent);
      const rotation = rotationFromHandleDrag(overlay.bounds, [
        latlng.lat,
        latlng.lng,
      ]);
      if (rotation !== null) {
        setOverlayRotation(overlay.id, rotation);
      }
    };
    const stopDrag = () => {
      L.DomEvent.off(document, "mousemove", handleMove);
      L.DomEvent.off(document, "mouseup", handleUp);
      window.removeEventListener("blur", stopDrag);
      bodyDragCleanupRef.current = null;
      map.dragging.enable();
    };
    const handleUp = (upEvent) => {
      if (upEvent.button !== 0) return;
      stopDrag();
    };
    L.DomEvent.on(document, "mousemove", handleMove);
    L.DomEvent.on(document, "mouseup", handleUp);
    window.addEventListener("blur", stopDrag);
    bodyDragCleanupRef.current = stopDrag;
  };

  const cornerMarkers = Object.keys(corners).map((corner) => {
    const key = `corner_${corner}`;
    const position =
      draggingKey === key ? frozenPositionRef.current : corners[corner];
    return (
      <Marker
        key={key}
        position={position}
        draggable
        icon={CORNER_ICONS[corner]}
        eventHandlers={{
          dragstart: () => startHandleDrag(key, corners[corner]),
          drag: (e) => {
            const point = e.target.getLatLng();
            setOverlayBounds(
              overlay.id,
              scaleBoundsFromCorner(
                overlay.bounds,
                corner,
                unrotatePoint(overlay.bounds, overlay.rotation, [
                  point.lat,
                  point.lng,
                ])
              )
            );
          },
          dragend: endHandleDrag,
        }}
      />
    );
  });

  const edgeMarkers = Object.keys(edges).map((edge) => {
    const key = `edge_${edge}`;
    const position =
      draggingKey === key ? frozenPositionRef.current : edges[edge];
    return (
      <Marker
        key={key}
        position={position}
        draggable
        icon={EDGE_ICONS[edge]}
        eventHandlers={{
          dragstart: () => startHandleDrag(key, edges[edge]),
          drag: (e) => {
            const point = e.target.getLatLng();
            setOverlayBounds(
              overlay.id,
              stretchBoundsEdge(
                overlay.bounds,
                edge,
                unrotatePoint(overlay.bounds, overlay.rotation, [
                  point.lat,
                  point.lng,
                ])
              )
            );
          },
          dragend: endHandleDrag,
        }}
      />
    );
  });

  const rotateMarker = (
    <Marker
      position={rotateHandle}
      icon={ROTATE_ICON}
      eventHandlers={{ mousedown: onRotateMouseDown }}
    >
      <Tooltip direction="top" offset={[0, -10]}>
        Rotate
      </Tooltip>
    </Marker>
  );

  return (
    <>
      <Polygon
        positions={[corners.nw, corners.ne, corners.se, corners.sw]}
        pathOptions={{
          className: styles.overlay_edit_border,
          weight: 2,
          dashArray: "6 6",
          fill: true,
          fillOpacity: 0,
        }}
        eventHandlers={{ mousedown: onBodyMouseDown }}
      />
      {cornerMarkers}
      {edgeMarkers}
      {rotateMarker}
    </>
  );
};

export default ImageOverlayEditor;
